import { useEffect, useRef, type ReactNode } from 'react';
import { gsap, ScrollTrigger } from '../lib/gsapContext';
import MagneticButton from '../components/MagneticButton';
import LiveSignals from '../components/LiveSignals';
import CaseStudies, { hasCaseStudies } from '../components/CaseStudies';
import { useDocumentMeta } from '../lib/useDocumentMeta';

/**
 * Every CTA on this page reads from these two constants, so the destination is a
 * one-line change.
 *
 * Destination: /introductions/book — the introductions-only booking route. It
 * asks for name, email and firm name, then books the 20-minute intro call. It
 * deliberately never touches /book (the AI Diagnostic funnel, with its budget
 * and revenue questions and not-a-fit message).
 */
const CTA_TO = '/introductions/book';
const CTA_LABEL = 'Book a call';

/**
 * PLACEHOLDER: founder name is unresolved across the business.
 * About says Yousif Mohamed, proposals say Yousif Idris, LinkedIn says Yussef
 * Idris. Matching the About page for now so the two agree.
 */
const FOUNDER_NAME = 'Yousif Mohamed';

const whatThisIs = [
  'A warm introduction to a named decision maker who has agreed to speak with you',
  'Access to companies with a live, funded reason to move now',
  'A written standard for what reaches you, agreed with you first',
  'Direct contact from the moment we step out of the way',
];

const whatThisIsNot = [
  'Booked meetings, calendar slots or show rates',
  'A purchased list, or contacts sent on volume',
  'Commission, revenue share or a mark up on your work',
  'Anyone sitting between you and the client once the conversation is real',
];

const moves = [
  {
    n: '01',
    title: 'Signal',
    body: 'A company only needs an outside partner when something has actually happened. A launch, a new market, a funding round, a change of leadership, a rebuild of the brand. Signals are read, not prospected. If we are guessing who might need something one day, we are not doing this properly. We work from what moved this week.',
  },
  {
    n: '02',
    title: 'Match',
    body: 'We hold both sides of the market in the same view. What each firm is genuinely strong at, the size of work worth their time, and which live need actually fits. Matching is the work. Searching is not.',
  },
  {
    n: '03',
    title: 'Route',
    body: 'The introduction goes to one best fit partner, on a thread, with the context both sides need to have a useful first conversation. Then we step back. You scope it, pitch it, contract it and invoice it yourself.',
  },
];

const criteria = [
  {
    n: '01',
    pre: 'There is a ',
    strong: 'live commercial trigger',
    post: '. Something has actually happened, and it is dated.',
  },
  {
    n: '02',
    pre: 'An ',
    strong: 'external requirement is genuinely open',
    post: '. The work is not already committed internally or to an incumbent.',
  },
  {
    n: '03',
    pre: 'There is a ',
    strong: 'credible budget',
    post: ', at or above the project floor you set.',
  },
  {
    n: '04',
    pre: 'We know ',
    strong: 'who owns the decision',
    post: ', by name and role.',
  },
  {
    n: '05',
    pre: 'That person ',
    strong: 'has agreed to speak with you',
    post: '.',
  },
];

const timeline = [
  {
    when: 'Days 1 to 5',
    title: 'Brief and criteria',
    body: 'We agree the target profile in writing. Category, project floor, geography, and precisely what counts as qualified. You sign it off before anything starts.',
  },
  {
    when: 'Days 5 to 30',
    title: 'Identify and qualify',
    body: 'We track companies across your agreed sectors for live triggers, then confirm the need is genuinely open before anything reaches you. Anything that qualifies is routed the moment it does.',
  },
  {
    when: 'Day 30',
    title: 'Review',
    body: 'We go through the work done, the evidence behind each qualification, and every introduction made. You decide whether there is a month two.',
  },
];

const stances = [
  {
    title: 'We introduce. You own the relationship.',
    body: 'We make the introduction and then step back. You scope, pitch, contract, deliver and invoice directly. No commission, no mark up, and no involvement in your commercial terms at any stage.',
  },
  {
    title: 'Routed, not broadcast.',
    body: 'Each qualified opportunity goes to one best fit partner first. We do not circulate the same opportunity around several firms at once. Any broader exclusivity is a separate agreement in writing, not something implied by this one. We take on a small number of firms per sector at a time.',
  },
];

const sides = [
  {
    who: 'If you deliver the work',
    title: 'Specialist firms and studios',
    body: 'You get access to companies with a live, funded need that fits what you are genuinely strong at, at or above the project size worth your team\'s time. Not more volume. The right briefs.',
  },
  {
    who: 'If you need the work done',
    title: 'Companies with something live',
    body: 'You get a small number of firms qualified against your brief, and the introductions coordinated. No procurement exercise, no open tender, no sifting through anyone who answered an advert.',
  },
];

const outcomes = [
  { n: '01', body: 'There is a fit, and we agree how it starts.' },
  { n: '02', body: 'There is not, and we leave it there.' },
  { n: '03', body: 'There is one specific thing to resolve, and we name it and put a date on it.' },
];

export default function Introductions() {
  useDocumentMeta({
    title: 'Introductions — Samia Systems',
    description:
      'Samia Systems connects businesses. We identify companies with a live, funded commercial need, and we introduce them, warm, to the specialist firms that can actually deliver the work.',
  });

  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scope = pageRef.current;
    if (!scope) return;
    const ctx = gsap.context(() => {
      // Hero entrance
      gsap.fromTo(
        ['.intro-label', '.intro-h1', '.intro-p', '.intro-cta'],
        { opacity: 0, y: 28 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.13, ease: 'power2.out', delay: 0.1 }
      );

      // Each block fades up as it scrolls into view
      ScrollTrigger.batch('.intro-block', {
        start: 'top 86%',
        once: true,
        onEnter: (els) =>
          gsap.fromTo(
            els,
            { opacity: 0, y: 24 },
            { opacity: 1, y: 0, duration: 0.65, stagger: 0.1, ease: 'power2.out' }
          ),
      });
    }, scope);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={pageRef}>
      {/* Hero */}
      <section className="grain relative bg-black px-6 pb-20 pt-40">
        <div className="container-x">
          <p className="intro-label label mb-6 inline-flex items-center gap-3">
            <span className="h-px w-10 bg-gold" />
            Introductions
          </p>
          <h1 className="intro-h1 max-w-4xl font-display text-hero font-light text-white text-balance">
            We connect you to the right people, based on{' '}
            <span className="italic text-gold">fit and timing</span>.
          </h1>
          <p className="intro-p mt-8 max-w-2xl font-sans text-base text-white/85">
            Samia Systems connects businesses. We identify companies with a live,
            funded commercial need, and we introduce them, warm, to the specialist
            firms that can actually deliver the work.
          </p>

          <div className="intro-cta mt-12 flex flex-col gap-4 sm:flex-row">
            <MagneticButton to={CTA_TO}>{CTA_LABEL}</MagneticButton>
            <MagneticButton href="#qualified" variant="ghost">
              What qualified means
            </MagneticButton>
          </div>
        </div>
      </section>

      {/* 01. The model */}
      <Block eyebrow="The model" n="01">
        <h2 className="font-display text-section font-light text-white text-balance">
          You are buying access, not output.
        </h2>
        <p className="mt-6 max-w-2xl font-sans text-body text-white/85">
          Most business development sells you activity. Someone is paid, then they go
          and produce calls, lists or campaigns on your behalf, and you carry the risk
          that the activity turns into nothing.
        </p>
        <p className="mt-5 max-w-2xl font-sans text-body text-white/85">
          This is a different arrangement. We sit close to both sides of a market, we
          watch for the moment a real need goes live, and when the fit is genuine we
          open the thread. You are not paying us to start searching. You are paying
          for access to a flow that was already running before you arrived.
        </p>
        <p className="mt-5 max-w-2xl font-sans text-body text-white/85">
          We do not deliver the work, and we never will. That is deliberate rather
          than a limitation. The moment we take on delivery we become a competitor
          holding your client list, and the introduction stops being honest. We hold
          the position between the two sides and nothing else.
        </p>

        <div className="mt-10 grid gap-6 md:grid-cols-2">
          <Card>
            <p className="font-mono text-[0.65rem] uppercase tracking-label text-gold">
              What this is
            </p>
            <ul className="mt-6 flex flex-col gap-3">
              {whatThisIs.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 font-sans text-sm text-white/85"
                >
                  <span className="mt-2.5 inline-block h-px w-4 shrink-0 bg-gold" />
                  {item}
                </li>
              ))}
            </ul>
          </Card>
          <Card>
            <p className="font-mono text-[0.65rem] uppercase tracking-label text-stone">
              What this is not
            </p>
            <ul className="mt-6 flex flex-col gap-3">
              {whatThisIsNot.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 font-sans text-sm text-white/70"
                >
                  <span className="mt-2.5 inline-block h-px w-4 shrink-0 bg-stone" />
                  {item}
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </Block>

      {/* 02. The motion */}
      <Block eyebrow="The motion" n="02">
        <h2 className="font-display text-section font-light text-white text-balance">
          Signal. Match. Route.
        </h2>
        <p className="mt-6 max-w-2xl font-sans text-body text-white/85">
          Three moves, in order. Everything else is execution.
        </p>

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {moves.map((m) => (
            <Card key={m.n}>
              <p className="font-mono text-xs tracking-label text-gold">{m.n}</p>
              <h3 className="mt-4 font-display text-2xl font-light text-white">
                {m.title}
              </h3>
              <p className="mt-4 font-sans text-sm leading-relaxed text-white/85">
                {m.body}
              </p>
            </Card>
          ))}
        </div>

        <p className="mt-8 max-w-2xl font-sans text-caption text-stone">
          A good introduction is worth something to both sides before any money
          changes hands. A bad one costs us the relationship on both sides at once,
          which is why we would rather send you nothing in a given month than send you
          something weak.
        </p>
      </Block>

      {/* Live signals — proof of flow. Hidden until src/data/signals.json has entries. */}
      <LiveSignals />

      {/* Case studies — hidden until src/data/caseStudies.json has entries. */}
      <CaseStudies />

      {/* 03. The standard */}
      <Block eyebrow="The standard" n="03" id="qualified">
        <h2 className="font-display text-section font-light text-white text-balance">
          Qualified has a specific meaning here, and it is written down before we
          start.
        </h2>
        <p className="mt-6 max-w-2xl font-sans text-body text-white/85">
          Most people use the word to mean interested. We use it to mean five things
          are true at once, and we agree the definition with you in writing before a
          single company is approached.
        </p>

        <ol className="mt-10 flex flex-col">
          {criteria.map((c) => (
            <li
              key={c.n}
              className="grid gap-2 border-t border-white/10 py-5 sm:grid-cols-[56px_1fr] sm:gap-5"
            >
              <span className="font-mono text-xs tabular-nums tracking-label text-gold">
                {c.n}
              </span>
              <p className="max-w-2xl font-sans text-body text-white/85">
                {c.pre}
                <strong className="font-medium text-white">{c.strong}</strong>
                {c.post}
              </p>
            </li>
          ))}
        </ol>

        <p className="mt-8 max-w-2xl rounded-4xl border-l-2 border-gold bg-charcoal p-7 font-display text-2xl font-light leading-snug text-white">
          All five, or it never reaches you. The fifth is the difference between an
          introduction and a lead, and it is the part most people skip.
        </p>

        <h3 className="mt-12 font-display text-2xl font-light text-white">
          How the fifth one is actually done
        </h3>
        <p className="mt-4 max-w-2xl font-sans text-body text-white/85">
          The other side is approached first, and has to confirm interest before
          anything about it reaches you. Your time is never spent on a maybe, and you
          are never asked to chase someone who has not agreed to the conversation. It
          also means we cannot make an introduction on a signal alone, however good
          the signal looks.
        </p>
        <p className="mt-6 max-w-2xl font-sans text-caption text-stone">
          Nothing is sent on volume and nothing is sent speculatively.
        </p>
      </Block>

      {/* 04. The first month */}
      <Block eyebrow="The first month" n="04">
        <h2 className="font-display text-section font-light text-white text-balance">
          What the first thirty days look like.
        </h2>
        <p className="mt-6 max-w-2xl font-sans text-body text-white/85">
          You should be able to judge this on evidence rather than on how it felt.
        </p>

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {timeline.map((t) => (
            <Card key={t.when}>
              <p className="font-mono text-[0.65rem] uppercase tracking-label text-gold">
                {t.when}
              </p>
              <h3 className="mt-4 font-display text-xl font-light text-white">
                {t.title}
              </h3>
              <p className="mt-4 font-sans text-sm leading-relaxed text-white/85">
                {t.body}
              </p>
            </Card>
          ))}
        </div>
      </Block>

      {/* 05. How we work */}
      <Block eyebrow="How we work" n="05">
        <h2 className="font-display text-section font-light text-white text-balance">
          Two things we hold to.
        </h2>

        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {stances.map((s) => (
            <Card key={s.title}>
              <h3 className="font-display text-2xl font-light text-white">
                {s.title}
              </h3>
              <p className="mt-4 font-sans text-sm leading-relaxed text-white/85">
                {s.body}
              </p>
            </Card>
          ))}
        </div>

        <p className="mt-8 max-w-2xl font-sans text-caption text-stone">
          We do not promise results we cannot control. Where an introduction does not
          work out, we replace it at no cost. That is the whole answer. We will not
          guarantee a meeting, a contract or revenue, because none of those are ours
          to give.
        </p>
      </Block>

      {/* 06. Both sides */}
      <Block eyebrow="Both sides" n="06">
        <h2 className="font-display text-section font-light text-white text-balance">
          A market only works when both sides are real.
        </h2>
        <p className="mt-6 max-w-2xl font-sans text-body text-white/85">
          Most markets hold two sets of companies that already need each other and
          have simply not met. That gap is the entire business. We are not selling one
          side a theory about the other. We are talking to both, which is the only
          reason the matching is worth anything.
        </p>

        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {sides.map((s) => (
            <Card key={s.who}>
              <p className="font-mono text-[0.65rem] uppercase tracking-label text-stone">
                {s.who}
              </p>
              <h3 className="mt-4 font-display text-2xl font-light text-white">
                {s.title}
              </h3>
              <p className="mt-4 font-sans text-sm leading-relaxed text-white/85">
                {s.body}
              </p>
            </Card>
          ))}
        </div>
      </Block>

      {/* 07. Where we stand */}
      <Block eyebrow="Where we stand" n="07">
        <h2 className="font-display text-section font-light text-white text-balance">
          The honest position.
        </h2>
        <p className="mt-6 max-w-2xl font-sans text-body text-white/85">
          Samia Systems was founded by {FOUNDER_NAME}, a Chartered Engineer who spent
          eight years inside a large aerospace organisation, running programmes where the difference
          between a good outcome and an expensive one was almost always the quality of
          the process behind it. That work runs on written criteria, verification
          before commitment, and a documented standard for what counts as good enough.
          We apply the same discipline here. Every company is qualified against
          criteria agreed with you in advance, and nothing is introduced on hope.
        </p>
        {hasCaseStudies ? (
          <p className="mt-5 max-w-2xl font-sans text-body text-white/85">
            This is still a young lane, and we will not pretend otherwise. You would
            be among the first firms in it. The introductions made so far are{' '}
            <a
              href="#case-studies"
              className="underline decoration-white/20 underline-offset-4 transition-colors hover:text-gold"
            >
              set out above
            </a>
            , sector by sector, with company names held back.
          </p>
        ) : (
          <p className="mt-5 max-w-2xl font-sans text-body text-white/85">
            This is a new lane for us and we will not pretend otherwise. You would be
            among the first firms in it. There is no client list to show you, and we
            would rather say that than borrow someone else&apos;s results.
          </p>
        )}
        <p className="mt-5 max-w-2xl font-sans text-body text-white/85">
          A monthly retainer for priority access and ongoing sourcing in your lane,
          agreed with you before we approach anyone. Not commission, not a mark up,
          and never taken from your work.
        </p>
      </Block>

      {/* Closing */}
      <section id="start" className="grain relative bg-black pb-32 pt-8">
        <div className="container-x">
          <div className="intro-block border-t border-white/10 pt-16">
            <h2 className="max-w-2xl font-display text-section font-light text-white text-balance">
              A first conversation ends in one of three places.
            </h2>
            <p className="mt-6 max-w-2xl font-sans text-base text-white/85">
              Twenty minutes. We want to understand what you are strong at and what is
              worth your team&apos;s time. By the end we should both know which of
              these it is.
            </p>

            <ol className="mt-10 flex max-w-2xl flex-col">
              {outcomes.map((o) => (
                <li
                  key={o.n}
                  className="grid grid-cols-[28px_1fr] gap-4 border-t border-white/10 py-4"
                >
                  <span className="font-mono text-xs tabular-nums text-gold">
                    {o.n}
                  </span>
                  <span className="font-sans text-body text-white/85">{o.body}</span>
                </li>
              ))}
            </ol>

            <div className="mt-10">
              <MagneticButton to={CTA_TO}>{CTA_LABEL}</MagneticButton>
            </div>

            <p className="mt-8 font-sans text-caption text-stone">
              We will not send a proposal instead of having the conversation.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

/** The repeated section scaffold: a gold eyebrow and number in the left rail,
 *  content on the right. Stacks to a single column below md. */
function Block({
  eyebrow,
  n,
  id,
  children,
}: {
  eyebrow: string;
  n: string;
  id?: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="bg-black pt-8">
      <div className="container-x">
        <div className="intro-block grid gap-8 border-t border-white/10 pt-16 md:grid-cols-[180px_1fr] md:gap-16">
          <div>
            <p className="font-mono text-xs uppercase tracking-label text-gold">
              {eyebrow}
            </p>
            <p className="mt-2 font-mono text-xs tracking-label text-stone">{n}</p>
          </div>
          <div>{children}</div>
        </div>
      </div>
    </section>
  );
}

/** Standard site card surface. */
function Card({ children }: { children: ReactNode }) {
  return (
    <div className="grain overflow-hidden rounded-4xl border border-white/10 bg-charcoal p-8">
      {children}
    </div>
  );
}
